import { useCallback, useEffect, useRef, useState } from "react";
import api, { resolveUploadUrl } from "../api/api";
import { showToast } from "../components/layout/Toast";

export const WORKSPACE_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const MAX_IMAGE_MB = 15;          // keep in sync with UPLOAD_MAX_IMAGE_MB on the backend
const IMAGE_NOTE_MAX_WIDTH = 320; // initial on-canvas width; the user can resize afterwards
const STACK_OFFSET = 24;          // several images at once are fanned out, not stacked exactly

/**
 * Uploads images to the backend's uploads/ folder and places them on the workspace canvas
 * as image notes. Supports a file picker, drag-and-drop from the desktop, and Ctrl+V paste.
 * Once placed, an image is an ordinary note: select it and drag its "+" handle onto any open
 * PDF to connect it.
 */
export default function useWorkspaceImageUpload({ pdfId, workspaceRef, screenToWorld, getPages, addSnippet }) {
    const [uploadingCount, setUploadingCount] = useState(0);

    // Notes must sit on a workspace page; a point off every page is pulled onto the nearest one.
    const placeOnPage = useCallback((pos, width, height) => {
        const pages = getPages() || [];
        if (!pages.length) return pos;
        const fits = p => pos.x >= p.x && pos.x <= p.x + p.width && pos.y >= p.y && pos.y <= p.y + p.height;
        if (pages.some(fits)) return pos;
        const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, Math.max(lo, hi)));
        let best = pos;
        let bestDist = Infinity;
        pages.forEach(p => {
            const candidate = {
                x: clamp(pos.x, p.x + 16, p.x + p.width - width - 16),
                y: clamp(pos.y, p.y + 16, p.y + p.height - (Number(height) || 0) - 16),
            };
            const dist = Math.hypot(candidate.x - pos.x, candidate.y - pos.y);
            if (dist < bestDist) { bestDist = dist; best = candidate; }
        });
        return best;
    }, [getPages]);

    const visibleCenter = useCallback(() => {
        const rect = workspaceRef.current?.getBoundingClientRect();
        if (!rect) return { x: 100, y: 100 };
        return screenToWorld(rect.left + rect.width / 2, rect.top + rect.height / 3);
    }, [workspaceRef, screenToWorld]);

    const addImageFiles = useCallback(async (fileList, dropPos = null) => {
        const files = Array.from(fileList || []).filter(f => WORKSPACE_IMAGE_TYPES.includes(f.type));
        if (!files.length) {
            showToast("Only PNG, JPG, WEBP or GIF images can be added to the workspace.", "warning");
            return;
        }
        const base = dropPos || visibleCenter();
        let added = 0;
        setUploadingCount(n => n + files.length);

        for (const [i, file] of files.entries()) {
            try {
                if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
                    throw new Error(`"${file.name}" is larger than ${MAX_IMAGE_MB} MB`);
                }
                const { data } = await api.uploadImage(file);
                const width = Math.min(IMAGE_NOTE_MAX_WIDTH, data.width || IMAGE_NOTE_MAX_WIDTH);
                const height = data.width ? Math.round((width * data.height) / data.width) : "auto";
                // A drop lands where the cursor is; a picked/pasted image is centred in view.
                const target = {
                    x: base.x - (dropPos ? 0 : width / 2) + i * STACK_OFFSET,
                    y: base.y + i * STACK_OFFSET,
                };
                addSnippet({
                    type: "image",
                    src: resolveUploadUrl(data.path),
                    uploadPath: data.path,
                    pdf_id: pdfId,
                    width,
                    height,
                }, placeOnPage(target, width, height));
                added += 1;
            } catch (err) {
                console.error("Image upload failed:", err);
                showToast(err.response?.data?.detail || err.message || "Image upload failed", "error", 4000);
            } finally {
                setUploadingCount(n => n - 1);
            }
        }

        if (added) {
            showToast(
                `${added > 1 ? `${added} images` : "Image"} added — click it, then drag the + handle onto a PDF to connect.`,
                "success",
                5000
            );
        }
    }, [pdfId, addSnippet, placeOnPage, visibleCenter]);

    // Paste from the clipboard (screenshots etc.) — but never while the user is typing,
    // and not while the workspace is collapsed in PDF-only layout.
    const addImageFilesRef = useRef(addImageFiles);
    useEffect(() => { addImageFilesRef.current = addImageFiles; }, [addImageFiles]);
    useEffect(() => {
        const onPaste = (e) => {
            const target = e.target;
            if (target?.isContentEditable || target?.closest?.("input, textarea")) return;
            if (!workspaceRef.current?.getBoundingClientRect().width) return;
            const images = Array.from(e.clipboardData?.files || []).filter(f => WORKSPACE_IMAGE_TYPES.includes(f.type));
            if (!images.length) return;
            e.preventDefault();
            addImageFilesRef.current(images);
        };
        window.addEventListener("paste", onPaste);
        return () => window.removeEventListener("paste", onPaste);
    }, [workspaceRef]);

    return { addImageFiles, uploadingImages: uploadingCount > 0 };
}
